import { Button, Flex, Text, useDisclosure } from '@chakra-ui/react'
import { useCallback, useState } from 'react'

import assertDefined from '@/utils/assertDefined'
import { noOp } from '@/utils/helpers'

import { usePushNotifications } from '@/client/services/platform'

import ModalV3 from '@/components/UI/Modal/ModalV3'

function RequestNotificationsModal({
  open,
  title,
  body,
  onResponse,
}: {
  open: boolean
  title: string
  body: string
  onResponse: () => void
}): JSX.Element | null {
  const { toggleSubscription } = usePushNotifications()

  const onAccept = useCallback(async () => {
    await toggleSubscription()
    onResponse()
  }, [onResponse, toggleSubscription])

  return (
    <ModalV3
      size="2xl"
      open={open}
      title={title}
      body={() => <Text>{body}</Text>}
      rightFooterContent={() => (
        <Flex gap={4}>
          <Button onClick={onResponse} variant="danger-outline">
            No, thanks
          </Button>
          <Button onClick={onAccept}>Yes, please!</Button>
        </Flex>
      )}
      onClose={noOp}
      disableClose
      disableClickOutside
    />
  )
}

export default function useRequestNotificationsModal({
  title = 'Can we send you notifications?',
  body,
}: {
  title?: string
  body: string
}): {
  showRequestNotificationsModal: ({ onComplete }: { onComplete: () => void }) => void
  RequestNotificationsModal: () => JSX.Element
} {
  // The reason we use state to store the callback is because it yields a slightly more intuitive API,
  // where the consumer can keep the flow of logic in one place, e.g:
  // ```
  // const onSubmit = () => {
  //   setShowingForm(false)
  //   showRequestNotificationsModal({ onComplete: () => router.push(...) })
  // }
  //
  // return <>
  //  <Button onClick={onSubmit}>Submit</Button>
  //  <RequestNotificationsModal />
  // </>
  // ```
  //
  // As you can see, all the flow of events is in `onSubmit`.
  // The alternative is to have the consumer pass in the callback to the modal component itself:
  // ```
  // const onSubmit = () => {
  //   setShowingForm(false)
  //   showRequestNotificationsModal()
  // }
  //
  // return <>
  //  <Button onClick={onSubmit}>Submit</Button>
  //  <RequestNotificationsModal onResponse={() => router.push(...)} />
  // </>
  //
  // Which I think is a bit more confusing, because the consumer now has to keep track of logic in two places.
  const [onResponseCallback, setOnResponseCallback] = useState<(() => void) | null>(null)

  const { isSupported: browserSupportsWebPush, isEnabled: subscriptionEnabled } =
    usePushNotifications()

  const shouldShowModal = browserSupportsWebPush && !subscriptionEnabled

  const { isOpen, onOpen, onClose } = useDisclosure()

  return {
    showRequestNotificationsModal: ({ onComplete }) => {
      if (shouldShowModal) {
        setOnResponseCallback(() => onComplete)
        onOpen()
      } else {
        // If we don't need to show the modal, just execute the callback
        onComplete()
      }
    },
    RequestNotificationsModal: () => (
      <RequestNotificationsModal
        title={title}
        body={body}
        open={shouldShowModal && isOpen}
        onResponse={() => {
          onClose()

          // This will always be defined because we only show this modal once we call `showRequestNotificationsModal`
          assertDefined(onResponseCallback, 'onResponseCallback must be defined')

          onResponseCallback()
        }}
      />
    ),
  }
}
